/* ==========================================================================
   Kaufwerk – Produktseite: Variantenauswahl und Galerie
   ========================================================================== */
(function () {
  'use strict';

  const theme = (window.theme = window.theme || {});
  const strings = theme.strings || {};
  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- Variantenauswahl ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.sectionId = this.dataset.sectionId;
      this.productUrl = this.dataset.productUrl;
      this.root = this.closest('.product-section, [data-quick-add-product]') || document;
      this.addEventListener('change', (event) => this.onChange(event));
    }

    selectedOptions() {
      return Array.from(this.querySelectorAll('[data-option-group]')).map((group) => {
        const select = group.querySelector('select');
        if (select) return select.value;
        const checked = group.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange(event) {
      const target = event.target;
      const optionUrl =
        target.tagName === 'SELECT' ? target.selectedOptions[0] && target.selectedOptions[0].dataset.productUrl : target.dataset.productUrl;

      // Kombinierte Listings: Option gehört zu einem anderen Produkt
      if (optionUrl && optionUrl !== this.productUrl) {
        if (this.closest('[data-quick-add-product]')) {
          const button = document.createElement('quick-add-button');
          button.dataset.productUrl = optionUrl;
          button.innerHTML = '<button type="button" hidden></button>';
          document.body.appendChild(button);
          button.open().finally(() => button.remove());
        } else {
          window.location.href = optionUrl;
        }
        return;
      }

      if (target.type === 'radio') {
        const label = target.closest('fieldset')?.querySelector('[data-selected-value]');
        if (label) label.textContent = target.value;
      }

      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((value, index) => value === options[index]));

      this.updateForm(variant);
      this.dispatchEvent(new CustomEvent('variant:change', { bubbles: true, detail: { variant, sectionId: this.sectionId } }));

      if (!variant) return;

      if (this.hasAttribute('data-update-url')) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      this.renderSection(variant);
    }

    updateForm(variant) {
      const form = document.getElementById(this.dataset.formId);
      const input = form && form.querySelector('input[name="id"]');
      if (input && variant) {
        input.value = variant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      const button = this.root.querySelector(`#AddButton-${CSS.escape(this.sectionId)}`);
      if (button) {
        const label = button.querySelector('.button__label');
        if (!variant) {
          button.disabled = true;
          if (label) label.textContent = strings.unavailable;
        } else if (!variant.available) {
          button.disabled = true;
          if (label) label.textContent = strings.soldOut;
        } else {
          button.disabled = false;
          if (label) label.textContent = strings.addToCart;
        }
      }

      const dynamic = this.root.querySelector('[data-variant-update="dynamic-checkout"]');
      if (dynamic) dynamic.hidden = !variant || !variant.available;
    }

    async renderSection(variant) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const separator = this.productUrl.includes('?') ? '&' : '?';
      try {
        const response = await fetch(`${this.productUrl}${separator}variant=${variant.id}&section_id=${encodeURIComponent(this.sectionId)}`, {
          signal: this.abortController.signal,
        });
        const doc = parseHTML(await response.text());
        const id = CSS.escape(this.sectionId);

        ['Price', 'Inventory', 'Sku', 'Delivery'].forEach((prefix) => {
          const fresh = doc.getElementById(`${prefix}-${this.sectionId}`);
          const current = this.root.querySelector(`#${prefix}-${id}`);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        });

        const freshButton = doc.getElementById(`AddButton-${this.sectionId}`);
        const currentButton = this.root.querySelector(`#AddButton-${id}`);
        if (freshButton && currentButton) {
          currentButton.disabled = freshButton.hasAttribute('disabled');
          const freshLabel = freshButton.querySelector('.button__label');
          const currentLabel = currentButton.querySelector('.button__label');
          if (freshLabel && currentLabel) currentLabel.textContent = freshLabel.textContent.trim();
        }

        // Verfügbarkeit der Optionswerte aktualisieren
        const freshPicker = doc.querySelector('variant-picker');
        if (freshPicker) {
          freshPicker.querySelectorAll('label[for]').forEach((freshLabel) => {
            const currentLabel = this.querySelector(`label[for="${CSS.escape(freshLabel.getAttribute('for'))}"]`);
            if (currentLabel) {
              currentLabel.className = freshLabel.className;
              const hidden = freshLabel.querySelector('.visually-hidden');
              const currentHidden = currentLabel.querySelector('.visually-hidden');
              if (hidden && currentHidden) currentHidden.textContent = hidden.textContent;
              else if (hidden && !currentHidden) currentLabel.appendChild(hidden.cloneNode(true));
              else if (!hidden && currentHidden && !currentLabel.classList.contains('swatch-option')) currentHidden.remove();
            }
          });
          freshPicker.querySelectorAll('select').forEach((freshSelect) => {
            const currentSelect = this.querySelector(`#${CSS.escape(freshSelect.id)}`);
            if (!currentSelect) return;
            Array.from(freshSelect.options).forEach((option, index) => {
              if (currentSelect.options[index]) currentSelect.options[index].textContent = option.textContent;
            });
          });
        }
      } catch (error) {
        if (error.name !== 'AbortError') console.error(error);
      }
    }
  }
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* ---------- Galerie mit Zoom ---------- */
  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.slider = this.querySelector('[data-gallery-slides]');
      this.slides = Array.from(this.querySelectorAll('[data-media-id]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-thumb-target]'));
      this.counter = this.querySelector('[data-gallery-current]');
      this.zoomDialog = this.querySelector('[data-zoom-dialog]');
      if (!this.slider) return;

      this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.goTo(thumb.dataset.thumbTarget)));
      this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));

      this.slider.addEventListener(
        'scroll',
        () => {
          window.cancelAnimationFrame(this.frame);
          this.frame = window.requestAnimationFrame(() => this.syncFromScroll());
        },
        { passive: true }
      );

      this.querySelectorAll('[data-zoom-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => this.openZoom(Number(trigger.dataset.zoomTrigger), trigger));
      });
      this.initZoom();

      const root = this.closest('.product-section, [data-quick-add-product]');
      root?.addEventListener('variant:change', (event) => {
        const variant = event.detail && event.detail.variant;
        if (variant && variant.media) this.goTo(String(variant.media));
      });

      if (this.dataset.selectedMedia) {
        window.requestAnimationFrame(() => this.goTo(this.dataset.selectedMedia, true));
      }
    }

    isScrollable() {
      return this.slider.scrollWidth > this.slider.clientWidth + 2;
    }

    currentIndex() {
      return this.slides.findIndex((slide) => slide.classList.contains('is-active'));
    }

    step(direction) {
      const count = this.slides.length;
      const index = (Math.max(0, this.currentIndex()) + direction + count) % count;
      this.goTo(this.slides[index].dataset.mediaId);
    }

    goTo(mediaId, instant) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (!slide) return;
      if (this.isScrollable()) {
        this.slider.scrollTo({
          left: slide.offsetLeft - this.slider.offsetLeft,
          behavior: instant || reducedMotion() ? 'auto' : 'smooth',
        });
      }
      this.setActive(slide);
    }

    syncFromScroll() {
      if (!this.isScrollable()) return;
      const index = Math.round(this.slider.scrollLeft / this.slider.clientWidth);
      const slide = this.slides[index];
      if (slide) this.setActive(slide);
    }

    setActive(slide) {
      this.slides.forEach((s) => s.classList.toggle('is-active', s === slide));
      const mediaId = slide.dataset.mediaId;
      this.thumbs.forEach((thumb) => {
        if (thumb.dataset.thumbTarget === mediaId) {
          thumb.setAttribute('aria-current', 'true');
          const list = thumb.closest('.gallery__thumbs');
          if (list && list.scrollHeight > list.clientHeight) {
            const listRect = list.getBoundingClientRect();
            const thumbRect = thumb.getBoundingClientRect();
            if (thumbRect.top < listRect.top || thumbRect.bottom > listRect.bottom) {
              list.scrollTop += thumbRect.top - listRect.top - listRect.height / 2 + thumbRect.height / 2;
            }
          }
        } else {
          thumb.removeAttribute('aria-current');
        }
      });
      if (this.counter) this.counter.textContent = String(this.slides.indexOf(slide) + 1);
      // Videos anhalten, die nicht mehr sichtbar sind
      this.slides.forEach((s) => {
        if (s !== slide) s.querySelectorAll('video').forEach((video) => video.pause());
      });
    }

    initZoom() {
      if (!this.zoomDialog) return;
      this.zoomDialog.querySelectorAll('[data-zoom-item]').forEach((item) => {
        item.addEventListener('click', (event) => {
          const zoomed = item.classList.toggle('is-zoomed');
          if (zoomed) this.moveZoom(item, event);
        });
        item.addEventListener('pointermove', (event) => {
          if (item.classList.contains('is-zoomed')) this.moveZoom(item, event);
        });
        item.tabIndex = 0;
        item.setAttribute('role', 'button');
        item.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            item.classList.toggle('is-zoomed');
          }
        });
      });
      this.zoomDialog.addEventListener('dialog:close', () => {
        this.zoomDialog.querySelectorAll('.is-zoomed').forEach((item) => item.classList.remove('is-zoomed'));
      });
    }

    moveZoom(item, event) {
      const image = item.querySelector('img');
      if (!image) return;
      const rect = item.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 100;
      const y = ((event.clientY - rect.top) / rect.height) * 100;
      image.style.transformOrigin = `${Math.max(0, Math.min(100, x))}% ${Math.max(0, Math.min(100, y))}%`;
    }

    openZoom(index, opener) {
      if (!this.zoomDialog || !theme.dialog) return;
      theme.dialog.open(this.zoomDialog, opener);
      const item = this.zoomDialog.querySelector(`[data-zoom-item="${index}"]`);
      if (item) {
        window.requestAnimationFrame(() => {
          item.scrollIntoView({ block: 'start' });
          item.focus({ preventScroll: true });
        });
      }
    }
  }
  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
})();
